import React, { useEffect, useMemo, useRef, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import FormControl from '@mui/material/FormControl';
import 'react-day-picker/lib/style.css';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { Stack } from '@mui/system';
import Alert from '@mui/material/Alert';
import { SelectedItems } from './config';
import './SearchFilters.css';
import { useAppContext } from '../Store';
import {
  clearSearchFilters,
  setCheckboxFilter,
  SET_FILTERS_FROM_SAVED,
  setSelectFilter,
  setRadioAndDatePickerFilter,
} from '../reducer';
import Button from '../components/styledComponents/Button';
import MultipleSelect from '../components/styledComponents/MultipleSelect';
import AutoCompleteMultipleSelect from '../components/styledComponents/MultipleSelect/AutoCompleteMultipleSelect';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import { useGetOrg } from '../services/getOrg';
import { stateCodeArray } from '../constants';
import {
  Box,
  Divider,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { loadSavedFilters, saveFiltersToStorage } from '../utils/helper';
import {
  premiumBearingOptions,
  variableStaticOptions,
} from '../components/DocEditContent/constants';
import { cloneDeep } from 'lodash';

export type LocalStorageFilter = {
  isActive: boolean;
  name: string;
  filters: SearchFilters;
};

const datePickerStyles = {
  'fieldset.MuiOutlinedInput-notchedOutline': {
    borderColor: '#ced4da',
  },
  ':hover fieldset.MuiOutlinedInput-notchedOutline': {
    borderColor: '#ced4da',
  },
  '.Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
    borderColor: '#80bdff',
  },
  '& label.Mui-focused': {
    color: '#80bdff',
  },
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    height: '30px',
    fontSize: '0.875rem',
    padding: 0,
  },
}));

const checkboxLabelSx = {
  '.MuiFormControlLabel-label': {
    fontSize: '0.8rem',
  },
  mr: '0.25',
  ml: -0.25,
  mt: 0.25,
  mb: 0.25,
  '.MuiCheckbox-root': {
    p: 0,
  },
};

const SearchFilters = () => {
  const { dispatch, state } = useAppContext();
  const { filters } = state.search;
  const { data: orgData } = useGetOrg();

  //local storage filters state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);
  const [savedFilters, setSavedFilters] = useState<LocalStorageFilter[]>(loadSavedFilters);
  const [showAddFilter, setShowAddFilter] = useState<boolean>(false);
  const [addFilterName, setAddFilterName] = useState<string>('');
  const addFilterTextFieldRef = useRef<HTMLDivElement | null>(null);
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  useEffect(() => {
    if (showAddFilter && addFilterTextFieldRef.current) {
      addFilterTextFieldRef.current.focus();
    }
  }, [showAddFilter]);

  useEffect(() => {
    saveFiltersToStorage(savedFilters);
  }, [savedFilters]);

  const multiSelectOptions = useMemo(() => {
    return orgData?.hierarchyOptionsWithLOB
      .filter((x) => !x.archived)
      .map((x) => {
        let label = x.label.split(' > ');
        let value = x.value.split('|');

        label.pop();
        value.pop();

        return { label: label.join(' > '), value: value.join('|') };
      })
      .filter(
        (option: Option, index: number, array) =>
          index === array.findIndex((o: Option) => o.label === option.label)
      );
  }, [orgData]);

  const handleCheckbox = (
    field: SearchFilterId,
    option: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { checked } = event.target;
    dispatch(setCheckboxFilter(field, option, checked, filters));
  };

  const handleSelect = (id: SearchFilterId, items: SelectedItems) => {
    dispatch(setSelectFilter(id, items, filters));
  };

  const handleRadioAndDatePicker = (newValue: any, id: SearchFilterId) => {
    dispatch(setRadioAndDatePickerFilter(newValue, id, filters));
  };

  const handleSaveFilter = () => {
    const filterToStore = {
      isActive: true,
      name: addFilterName,
      filters: cloneDeep(filters),
    };

    setSavedFilters((prevState) => {
      let newState = [...prevState, filterToStore].map((f, i, arr) => ({
        ...f,
        isActive: i === arr.length - 1,
      }));
      return newState;
    });
  };

  const handleUpdateFilter = () => {
    setIsUpdateLoading(true);
    setSavedFilters((prevState) => {
      let newState = [...prevState];
      let index = newState.findIndex((f: LocalStorageFilter) => f.isActive);
      newState[index] = { ...newState[index], filters: cloneDeep(filters) };
      return newState;
    });
    setTimeout(() => {
      setIsUpdateLoading(false);
      setSnackbarOpen(true);
    }, 1000);
  };

  const handleLoadFilter = (f: LocalStorageFilter, index: number) => {
    if (state.isLoading) return;

    if (f.isActive) {
      handleClearFilters();
      return;
    }

    dispatch({
      type: SET_FILTERS_FROM_SAVED,
      payload: cloneDeep(f.filters),
    });
    setSavedFilters((prevState) => {
      let newState = prevState.map((f, i) => ({ ...f, isActive: index === i }));
      return newState;
    });
  };

  const handleDeleteFilter = (index: number) => {
    if (savedFilters[index].isActive) {
      dispatch(clearSearchFilters(state));
    }
    setSavedFilters((prevState: LocalStorageFilter[]) => {
      let newState = [...prevState];
      newState.splice(index, 1);
      return newState;
    });
  };

  const handleClearFilters = () => {
    dispatch(clearSearchFilters(state));
    setSavedFilters((prevState) => {
      let newState = prevState.map((f) => ({ ...f, isActive: false }));
      return newState;
    });
  };

  const updateFilterSnackbar = () => (
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={3000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setSnackbarOpen(false)}
        severity={'success'}
        sx={{
          width: 0.5,
          border: '2px solid',
          borderColor: 'success.light',
        }}
      >
        Filter Updated Successfully
      </Alert>
    </Snackbar>
  );

  return (
    <div>
      {updateFilterSnackbar()}
      <form className="content-box search-filters" style={{ padding: '1rem', width: '75%' }}>
        <Grid container columnSpacing={{ mobile: 2, desktop: 2 }} rowGap={0.5} className="grid">
          {(savedFilters.length > 0 || showAddFilter) && (
            <Stack width={'100%'} gap={2}>
              <Stack marginLeft={2}>
                <Typography fontSize={16} fontWeight={600}>
                  Your Filters
                </Typography>
                <Divider />
              </Stack>
              <Stack
                direction={'row'}
                width={'100%'}
                spacing={2}
                marginLeft={2}
                marginTop={'-8px'}
                marginBottom="8px"
                alignItems={'center'}
              >
                {savedFilters.map((obj: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      display: 'inline-block',
                      margin: 1,
                      width: '18%',
                      '&:hover .clear-icon': {
                        opacity: 0.5,
                        color: 'black',
                      },
                      '& .clear-icon:hover': {
                        opacity: 1,
                      },
                    }}
                  >
                    <ClearIcon
                      fontSize="small"
                      className="clear-icon"
                      onClick={() => handleDeleteFilter(index)}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        zIndex: 2,
                        cursor: 'pointer',
                        color: 'black',
                        opacity: 0,
                        transition: 'opacity 0.2s ease-in-out, color 0.2s ease-in-out',
                        pointerEvents: 'auto',
                      }}
                    />
                    <Button
                      fullWidth
                      sx={{
                        height: '30px',
                        justifyContent: 'center',
                      }}
                      variant={obj.isActive ? 'contained' : 'outlined'}
                      onClick={() => handleLoadFilter(obj, index)}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {obj.name}
                      </span>
                    </Button>
                  </Box>
                ))}

                {showAddFilter && (
                  <StyledTextField
                    value={addFilterName}
                    onChange={(e) => setAddFilterName(e.target.value)}
                    inputProps={{ maxLength: 25 }}
                    inputRef={addFilterTextFieldRef}
                    sx={{ width: '18%' }}
                    placeholder={`New Filter ${savedFilters.length + 1}`}
                    onBlur={() => {
                      if (addFilterName.length > 0) {
                        handleSaveFilter();
                      }
                      setShowAddFilter(false);
                      setAddFilterName('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (addFilterName.length > 0) {
                          handleSaveFilter();
                        }
                        setShowAddFilter(false);
                        setAddFilterName('');
                      }
                    }}
                  />
                )}
                {savedFilters.length < 5 && !showAddFilter && (
                  <IconButton
                    size="small"
                    style={{ padding: 0 }}
                    aria-label="add-filter"
                    onClick={() => {
                      setShowAddFilter(true);
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          )}
          <Grid item mobile={12} tablet={6}>
            <Grid container rowGap={0.5}>
              <Grid item mobile={12} height={'116.59px'}>
                <AutoCompleteMultipleSelect
                  options={multiSelectOptions ?? []}
                  selectedValue={filters.hierarchy}
                  name="hierarchy"
                  dispatchFilter={handleSelect}
                  selectAll
                  label="Business Unit > Sub Business Unit"
                  grouped
                />
              </Grid>
              <Grid item mobile={12}>
                <MultipleSelect
                  label="Line of Business"
                  options={orgData?.lobOptions ?? []}
                  selectedValue={filters.line_of_business}
                  name="line_of_business"
                  dispatchFilter={handleSelect}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item mobile={12} tablet={6}>
            <Grid container columnSpacing={1}>
              <Grid item mobile={12} desktop={4}>
                <MultipleSelect
                  label="Form source"
                  options={orgData?.formSourceOptions ?? []}
                  name="form_source"
                  selectedValue={filters.form_source}
                  dispatchFilter={handleSelect}
                />
              </Grid>

              <Grid item mobile={12} desktop={4}>
                <MultipleSelect
                  label="Form type"
                  options={orgData?.formTypeOptions ?? []}
                  name="form_type"
                  selectedValue={filters.form_type}
                  dispatchFilter={handleSelect}
                />
              </Grid>

              <Grid item mobile={12} desktop={4}>
                <MultipleSelect
                  label="Approved States"
                  options={stateCodeArray.map((x) => ({ label: x.label, value: x.key }))}
                  name="state_filed"
                  selectedValue={filters.state_filed}
                  dispatchFilter={handleSelect}
                />
              </Grid>
              <Grid item mobile={12} desktop={4}>
                <label className="search-form-label">Admitted / Non-Admitted</label>
                <Stack direction="column">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        id="admitted"
                        name="Admitted"
                        checked={filters.form_type_of_business.has('Admitted')}
                        onChange={(e: any) =>
                          handleCheckbox('form_type_of_business', 'Admitted', e)
                        }
                      />
                    }
                    sx={checkboxLabelSx}
                    label="Admitted"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        id="non-admitted"
                        name="Non-Admitted"
                        checked={filters.form_type_of_business.has('Non-Admitted')}
                        onChange={(e: any) =>
                          handleCheckbox('form_type_of_business', 'Non-Admitted', e)
                        }
                      />
                    }
                    sx={checkboxLabelSx}
                    label="Non-Admitted"
                  />
                </Stack>
              </Grid>
              <Grid item mobile={12} desktop={8}>
                <label className="search-form-label">Form Status</label>
                <Stack direction={'column'}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        id="active"
                        name="Active"
                        checked={filters.form_status.has('Active')}
                        onChange={(e: any) => handleCheckbox('form_status', 'Active', e)}
                      />
                    }
                    sx={checkboxLabelSx}
                    label="Active"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        id="inactive"
                        name="Inactive"
                        checked={filters.form_status.has('Inactive')}
                        onChange={(e: any) => handleCheckbox('form_status', 'Inactive', e)}
                      />
                    }
                    sx={checkboxLabelSx}
                    label="Inactive (Includes retired or expired forms)"
                  />
                </Stack>
              </Grid>
            </Grid>
          </Grid>

          {showAdvancedFilters && (
            <>
              <Grid item mobile={12} desktop={6}>
                <Grid container rowGap={0.5}>
                  <Grid
                    container
                    mobile={12}
                    desktop={12}
                    display={'flex'}
                    justifyContent={'flex-start'}
                    direction={'column'}
                  >
                    <label className="search-form-label">Effective Date</label>
                    <Stack direction="row" spacing={2}>
                      <DatePicker
                        slotProps={{ textField: { size: 'small' } }}
                        label="From"
                        value={
                          filters.state_effective_date_min
                            ? moment(filters.state_effective_date_min)
                            : null
                        }
                        maxDate={
                          filters.state_effective_date_max &&
                          moment(filters.state_effective_date_max).subtract(1, 'd')
                        }
                        onChange={(newValue) => {
                          handleRadioAndDatePicker(newValue, 'state_effective_date_min');
                        }}
                        sx={datePickerStyles}
                      />

                      <DatePicker
                        slotProps={{ textField: { size: 'small' } }}
                        label="To"
                        value={
                          filters.state_effective_date_max
                            ? moment(filters.state_effective_date_max)
                            : null
                        }
                        minDate={
                          filters.state_effective_date_min &&
                          moment(filters.state_effective_date_min).add(1, 'd')
                        }
                        onChange={(newValue) => {
                          handleRadioAndDatePicker(newValue, 'state_effective_date_max');
                        }}
                        sx={datePickerStyles}
                      />
                    </Stack>
                  </Grid>
                  <Grid
                    container
                    mobile={12}
                    desktop={12}
                    display={'flex'}
                    justifyContent={'flex-start'}
                    direction={'column'}
                  >
                    <label className="search-form-label">Expiration Date</label>
                    <Stack direction="row" spacing={2}>
                      <DatePicker
                        slotProps={{ textField: { size: 'small' } }}
                        label="From"
                        value={
                          filters.state_expired_date_min
                            ? moment(filters.state_expired_date_min)
                            : null
                        }
                        maxDate={
                          filters.state_expired_date_max &&
                          moment(filters.state_expired_date_max).subtract(1, 'd')
                        }
                        onChange={(newValue) => {
                          handleRadioAndDatePicker(newValue, 'state_expired_date_min');
                        }}
                        sx={datePickerStyles}
                      />
                      <DatePicker
                        slotProps={{ textField: { size: 'small' } }}
                        label="To"
                        value={
                          filters.state_expired_date_max
                            ? moment(filters.state_expired_date_max)
                            : null
                        }
                        minDate={
                          filters.state_expired_date_min &&
                          moment(filters.state_expired_date_min).add(1, 'd')
                        }
                        onChange={(newValue) => {
                          handleRadioAndDatePicker(newValue, 'state_expired_date_max');
                        }}
                        sx={datePickerStyles}
                      />
                    </Stack>
                  </Grid>
                  <Grid item mobile={12} desktop={12}>
                    <MultipleSelect
                      label="Carrier"
                      options={
                        orgData?.carrierOptions.map((c) => {
                          return { label: c.value, value: c.value };
                        }) ?? []
                      }
                      name="carrier"
                      selectedValue={filters.carrier}
                      dispatchFilter={handleSelect}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item mobile={12} desktop={6}>
                <Grid container columnSpacing={1}>
                  <Grid item mobile={12} tablet={4}>
                    <FormControl>
                      <label className="search-form-label">Premium Bearing</label>
                      <RadioGroup
                        row
                        sx={{ justifyContent: 'space-between' }}
                        value={filters.premium_bearing}
                        onChange={(_, val) => {
                          handleRadioAndDatePicker(val, 'premium_bearing');
                        }}
                      >
                        {premiumBearingOptions.map((obj, index) => (
                          <FormControlLabel
                            key={index}
                            value={obj.value}
                            control={<Radio size="small" sx={{ ml: '-8px' }} />}
                            label={obj.label}
                            sx={checkboxLabelSx}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item mobile={12} tablet={8}>
                    <FormControl>
                      <label className="search-form-label">Variable/Static</label>
                      <RadioGroup
                        row
                        sx={{ justifyContent: 'space-between' }}
                        value={filters.has_fields ? 'Variable' : 'Static'}
                        onChange={(_, val) => {
                          handleRadioAndDatePicker(val === 'Variable' ? true : false, 'has_fields');
                        }}
                      >
                        {variableStaticOptions.map((obj, index) => (
                          <FormControlLabel
                            key={index}
                            value={obj.value}
                            control={<Radio size="small" sx={{ ml: '-8px' }} />}
                            label={obj.label}
                            sx={checkboxLabelSx}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item mobile={12} desktop={4}>
                    <div>
                      <label className="search-form-label">Impact on Coverage</label>
                      <Stack>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                id="b"
                                name="brc"
                                checked={filters.impact_on_coverage.has('Broadens Coverage')}
                                onChange={(e: any) =>
                                  handleCheckbox('impact_on_coverage', 'Broadens Coverage', e)
                                }
                              />
                            }
                            sx={checkboxLabelSx}
                            label="Broadens"
                          />
                        </div>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                id="r"
                                name="brc"
                                checked={filters.impact_on_coverage.has('Restricts Coverage')}
                                onChange={(e: any) =>
                                  handleCheckbox('impact_on_coverage', 'Restricts Coverage', e)
                                }
                              />
                            }
                            sx={checkboxLabelSx}
                            label="Restricts"
                          />
                        </div>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                id="c"
                                name="brc"
                                checked={filters.impact_on_coverage.has('Clarifies Coverage')}
                                onChange={(e: any) =>
                                  handleCheckbox('impact_on_coverage', 'Clarifies Coverage', e)
                                }
                              />
                            }
                            sx={checkboxLabelSx}
                            label="Clarifies"
                          />
                        </div>
                      </Stack>
                    </div>
                  </Grid>
                  <Grid item mobile={12} desktop={4}>
                    <div>
                      <label className="search-form-label">Form Category</label>
                      <Stack direction={'column'}>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                id="m"
                                name="mform"
                                checked={filters.form_category.has('M-Form')}
                                onChange={(e: any) => handleCheckbox('form_category', 'M-Form', e)}
                              />
                            }
                            sx={checkboxLabelSx}
                            label="M-Form"
                          />
                        </div>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                id="p"
                                name="pform"
                                checked={filters.form_category.has('Permanent Form')}
                                onChange={(e: any) =>
                                  handleCheckbox('form_category', 'Permanent Form', e)
                                }
                              />
                            }
                            sx={checkboxLabelSx}
                            label="Permanent Form"
                          />
                        </div>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                id="t"
                                name="tform"
                                checked={filters.form_category.has('T-Form')}
                                onChange={(e: any) => handleCheckbox('form_category', 'T-Form', e)}
                              />
                            }
                            sx={checkboxLabelSx}
                            label="T-Form"
                          />
                        </div>
                      </Stack>
                    </div>
                  </Grid>
                  <Grid item mobile={12} desktop={4}>
                    <div>
                      <label className="search-form-label">Usage Category</label>
                      <Stack direction={'column'}>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                id="m"
                                name="mco"
                                checked={filters.form_usage_category.has('Mandatory')}
                                onChange={(e: any) =>
                                  handleCheckbox('form_usage_category', 'Mandatory', e)
                                }
                              />
                            }
                            sx={checkboxLabelSx}
                            label="Mandatory"
                          />
                        </div>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                id="co"
                                name="mco"
                                checked={filters.form_usage_category.has('Conditional')}
                                onChange={(e: any) =>
                                  handleCheckbox('form_usage_category', 'Conditional', e)
                                }
                              />
                            }
                            sx={checkboxLabelSx}
                            label="Conditional"
                          />
                        </div>
                        <div>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                id="o"
                                name="mco"
                                checked={filters.form_usage_category.has('Optional')}
                                onChange={(e: any) =>
                                  handleCheckbox('form_usage_category', 'Optional', e)
                                }
                              />
                            }
                            sx={checkboxLabelSx}
                            label="Optional"
                          />
                        </div>
                      </Stack>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </form>
      <div
        className="content-box"
        style={{ width: '75%', padding: '8px 0px 8px 0px', marginTop: '1rem' }}
      >
        <Stack
          direction={'row'}
          spacing={2}
          marginLeft={2}
          marginRight={2}
          display={'flex'}
          alignItems={'center'}
        >
          {(savedFilters.length < 5 || savedFilters.some((f) => f.isActive)) && (
            <Button
              sx={{ width: '100px', height: '30px' }}
              aria-label="add-filter"
              onClick={() => {
                if (savedFilters.every((f) => !f.isActive)) {
                  setShowAddFilter(true);
                } else {
                  handleUpdateFilter();
                }
              }}
              loading={isUpdateLoading}
            >
              {savedFilters.every((f) => !f.isActive) ? 'Save Filter' : `Update Filter`}
            </Button>
          )}

          <div>
            <Button
              sx={{ width: '100px', height: '30px' }}
              onClick={() => handleClearFilters()}
              variant="outlined"
            >
              Clear Filter
            </Button>
          </div>
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              height: '100%',
            }}
          >
            <Button
              aria-label="close"
              onClick={() => {
                setShowAdvancedFilters((prevState) => !prevState);
              }}
              variant="text"
            >
              {!showAdvancedFilters ? 'Show ' : 'Hide '}
              Advanced Filters
            </Button>
          </div>
        </Stack>
      </div>
    </div>
  );
};

export default SearchFilters;
